/**
 * La Panoramica di progetto (`/progetto`) — la vetrina che legge Enzo.
 *
 * ## Revisione del 21-08-2026 (Enrico)
 *
 * La pagina di maggio aveva sei blocchi impilati e ne diceva tre volte la
 * stessa cosa. Ora ha una progressione: **riassunto → tempo → sostanza**.
 *
 *   [ Avanzamento · Prossima consegna · Prossimo incontro ]   i tre numeri
 *   [ Timeline (Gantt)              | Questa settimana   ]   il piano, l'adesso
 *   [ Ultime attività               | A che punto siamo  ]   il fatto, il quanto
 *
 * Cosa è uscito, e perché:
 *
 * - **«Cosa abbiamo fatto»** → diventa *Ultime attività*, con le righe
 *   cliccabili che aprono la spiegazione.
 * - **«Cosa serve da te»** → via. Con il KPI «Domande aperte» era il secondo
 *   posto in cui la pagina metteva compiti davanti a Enzo, e la regola su di lui
 *   è *solo controllo, mai compiti*. Le domande restano dietro la voce Bacheca.
 * - **«Aree di lavoro»** → via: lista piatta dei sette moduli col pallino di
 *   stato, ripeteva la voce Moduli in barra. Al suo posto *A che punto sono i
 *   moduli*, che aggiunge il quanto e spiega il numero in alto.
 * - **La banda dev** (`core@versione · ambiente`) → via: è diagnostica nostra,
 *   non roba da mostrare al cliente. La salute dei servizi sta in `/admin`.
 *
 * ## L'invariante da non rompere
 *
 * Il KPI «Avanzamento» e la casella «A che punto sono i moduli» vengono dalla
 * **stessa chiamata** (`/api/v1/avanzamento`): il primo è la media delle righe
 * della seconda. Due numeri che si contraddicono sulla stessa schermata
 * distruggono la fiducia in tutta la pagina — per questo non si calcolano in
 * due posti.
 */

import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import { Gantt } from "@/components/Gantt";
import { KpiStrip } from "@/components/KpiStrip";
import { PuntoModuli } from "@/components/progetto/PuntoModuli";
import { QuestaSettimana } from "@/components/progetto/QuestaSettimana";
import { UltimeAttivita } from "@/components/progetto/UltimeAttivita";
import { Skeleton } from "@/components/ui/skeleton";
import { useGantt } from "@/hooks/useGantt";
import { useModuli } from "@/hooks/useModuli";

export function HomePage() {
  const { data: moduli = [] } = useModuli();
  const { data: ganttElementi = [] } = useGantt();

  const oggi = new Date().toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="mx-auto max-w-7xl px-6 py-10 space-y-8"
    >
      <header className="space-y-1">
        <p className="text-xs text-muted-foreground capitalize">{oggi}</p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          Stato del progetto
        </h1>
      </header>

      {/* I tre numeri: dove siamo · cosa arriva · quando ci vediamo */}
      <KpiStrip />

      {/* Il tempo: il piano a sinistra, l'adesso a destra */}
      <section className="grid gap-4 lg:grid-cols-12">
        <div className="lg:col-span-8">
          {/* Il Gantt porta alla pagina intera, dove si legge meglio e si
              stampa. La freccia c'è perché una cosa cliccabile che non lo
              dichiara non la clicca nessuno — è lo stesso motivo per cui i
              moduli non si trovavano prima della barra laterale. */}
          <Link
            to="/progetto/gantt"
            className="block rounded-lg border bg-card h-full p-5 space-y-3 transition-colors hover:border-brand-700/40 group"
          >
            <div className="flex items-baseline justify-between">
              <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                Timeline
              </h2>
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1 group-hover:text-brand-700 transition-colors">
                apri a schermo intero
                <ArrowRight className="size-3 transition-transform group-hover:translate-x-0.5" />
              </span>
            </div>
            {ganttElementi.length > 0 && moduli.length > 0 ? (
              <Gantt elementi={ganttElementi} moduli={moduli} />
            ) : (
              <Skeleton className="h-72 w-full" />
            )}
          </Link>
        </div>

        <div className="lg:col-span-4">
          <QuestaSettimana />
        </div>
      </section>

      {/* La sostanza: cosa è stato fatto, e a che punto è ogni pezzo */}
      <section className="grid gap-4 md:grid-cols-2 items-start">
        <UltimeAttivita />
        <PuntoModuli />
      </section>
    </motion.div>
  );
}
